import React, { useEffect, useRef } from "react";

import { toast } from "sonner";

import UploadToast from "./UploadToast";
import { useUploadQueue } from "../../contexts/UploadQueue";

const SUCCESS_TOAST_DURATION_MS = 4000;

// Custom toasts are data-styled=false, so they carry no Sonner surface and must
// style themselves (see UploadToast). The app Toaster still concatenates its
// `toastOptions.classNames.toast` (p-4 / border / shadow) onto every toast, and
// Sonner's cn() doesn't tailwind-merge — so we neutralize that wrapper chrome
// with important utilities, leaving only our own card.
const NEUTRALIZE_WRAPPER_CLASSNAMES = {
  toast: "border-0! bg-transparent! p-0! shadow-none!",
};

/**
 * Syncs the global upload queue to Sonner toasts on the app's **default**
 * Toaster, so they cohabit with regular notifications in a single stack.
 *
 * Hybrid display: only the active upload (plus transient completed/error toasts)
 * gets a card; queued uploads are surfaced as a "+N en attente" badge on the
 * active toast rather than as separate toasts. Renders nothing itself.
 */
export default function UploadQueueToasts(): null {
  const { jobs, cancel, remove, retry } = useUploadQueue();
  const shownIdsRef = useRef<Set<string>>(new Set());
  const settledIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const queuedCount = jobs.filter((job) => job.status === "pending").length;
    // Queued uploads are represented by the "+N" badge, not separate toasts.
    const visibleJobs = jobs.filter((job) => job.status !== "pending");
    const visibleIds = new Set(visibleJobs.map((job) => job.id));

    shownIdsRef.current.forEach((jobId) => {
      if (!visibleIds.has(jobId)) {
        toast.dismiss(jobId);
        shownIdsRef.current.delete(jobId);
        settledIdsRef.current.delete(jobId);
      }
    });

    visibleJobs.forEach((job) => {
      // A completed job's success toast is shown once and auto-dismisses; don't
      // re-issue it on later queue changes (which would re-pop a dismissed one).
      if (job.status === "completed" && settledIdsRef.current.has(job.id)) {
        return;
      }
      shownIdsRef.current.add(job.id);
      if (job.status === "completed") settledIdsRef.current.add(job.id);
      toast.custom(
        () => (
          <UploadToast
            job={job}
            onCancel={cancel}
            onRemove={remove}
            onRetry={retry}
            queuedCount={job.status === "uploading" ? queuedCount : 0}
          />
        ),
        {
          dismissible: false,
          id: job.id,
          unstyled: true,
          classNames: NEUTRALIZE_WRAPPER_CLASSNAMES,
          duration:
            job.status === "completed" ? SUCCESS_TOAST_DURATION_MS : Infinity,
        },
      );
    });
  }, [jobs, cancel, remove, retry]);

  return null;
}
